import { useEffect, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { Subject, from, startWith, switchMap, delay, type Subscription, timer, map } from "rxjs";

import { NotaryPresenceStatuses } from "graphql_globals";
import { useMutation } from "util/graphql";
import { retryBackoff } from "util/rxjs";
import { socketChannel } from "socket/util";
import {
  CURRENT_EXPERIMENTS,
  EXPERIMENT_GROUP_DEFAULT,
  useExperimentGroup,
} from "common/experiments/group";

import UpdateNotaryPresenceMutation from "./update_notary_presence_mutation.graphql";

const DELAY_MS = 5_000;
const EXPERIMENT = CURRENT_EXPERIMENTS.NotaryPresence;
const MAX_RETRY_ATTEMPTS = 100;
const MUTATION_OPTIONS = { suppressAnalytics: true };

export function useOnlineHeartBeat({
  status,
  skip = false,
}: {
  status: NotaryPresenceStatuses;
  skip?: boolean;
}) {
  const experimentGroup = useExperimentGroup({
    flag: EXPERIMENT.flag,
    possibleGroups: [EXPERIMENT.A, EXPERIMENT.B],
    precedence: "ld",
  });

  const serializedStatus = status.toLowerCase() as Lowercase<NotaryPresenceStatuses>;
  const meetingId = useParams().meetingId;

  useGraphQLHeartBeat(status, meetingId, skip || experimentGroup === EXPERIMENT.A);
  useActionCableHeartBeat(
    serializedStatus,
    meetingId,
    skip || experimentGroup === EXPERIMENT_GROUP_DEFAULT,
  );
}

let graphQLSubscription: Subscription | undefined;

function useGraphQLHeartBeat(
  status: NotaryPresenceStatuses,
  meetingId: string | undefined,
  skip: boolean,
): void {
  const updateNotaryPresenceMutateFn = useMutation(
    UpdateNotaryPresenceMutation,
    undefined,
    MUTATION_OPTIONS,
  );
  useEffect(() => {
    if (graphQLSubscription || skip) {
      return;
    }

    const iter$ = new Subject();
    const variables = { input: { status, meetingId } };
    graphQLSubscription = iter$
      .pipe(
        startWith(null),
        delay(DELAY_MS),
        switchMap(() => from(updateNotaryPresenceMutateFn({ variables }))),
        retryBackoff({ maxAttempts: MAX_RETRY_ATTEMPTS }),
      )
      .subscribe(() => {
        iter$.next(null);
      });
    return () => {
      graphQLSubscription!.unsubscribe();
      graphQLSubscription = undefined;
    };
  }, [status, meetingId, updateNotaryPresenceMutateFn]);
}

let actionCableSubscription: Subscription | undefined;

function useActionCableHeartBeat(
  serializedStatus: Lowercase<NotaryPresenceStatuses>,
  meetingId: string | undefined,
  skip: boolean,
): void {
  useEffect(() => {
    if (actionCableSubscription || skip) {
      return;
    }

    actionCableSubscription = socketChannel({ channelName: "NotaryChannel" })
      .pipe(switchMap((channel) => timer(0, DELAY_MS).pipe(map(() => channel))))
      .subscribe((channel) => {
        channel.sendMessage<{ status: Lowercase<NotaryPresenceStatuses>; meeting_gid?: string }>(
          "update_presence",
          { status: serializedStatus, meeting_gid: meetingId },
        );
      });

    return () => {
      actionCableSubscription!.unsubscribe();
      actionCableSubscription = undefined;
    };
  }, [serializedStatus, meetingId]);
}

export function OnlineHeartBeatUnavailable<T extends Exclude<ReactNode, null | undefined>>({
  children,
}: {
  children: T;
}): T {
  useOnlineHeartBeat({ status: NotaryPresenceStatuses.UNAVAILABLE });
  return children;
}
